import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { CircleIndicator } from 'Components/Indicator/CircleIndicator';
import { ConfirmationModal } from 'Components/Modal/ConfirmationModal';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { CONTACT_INFORMATION_FOR_ERROR_MESSAGE, GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE, ICON_DELETE_REMOVE, ICON_NEGATIVE, ICON_POSITIVE } from 'Config/Icons';
import { makeComplianceRequirementHumanReadable } from 'Helpers/ComplianceRequirementFormatter/ComplianceRequirementFormatter';
import { controlTextToString, getFrameworkGroupControlParts, getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { ellipt } from 'Helpers/StringUtils';
import { AiOperationalControlAssessment, AiOperationalControlAssessmentGenerationStatus } from 'Models/ArtificialIntelligence';
import { ComplianceRequirementForControlResponse, ComplianceRequirementStatusValues, titleCaseRequirementStatus } from 'Models/ComplianceRequirements';
import { OperationalControl, effectivenessAsString, getEffectivenessVariantColor, numberAsEffectiveness } from 'Models/OperationalControls';

import styles from './ArtificialIntelligenceControlAssessment.module.css';

const MAX_CHARS_FOR_TOOLTIP = 300;

enum DisplayedModal {
    CLEAR,
    GENERATE,
}

export interface ArtificialIntelligenceControlAssessmentProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    complianceRequirements: ComplianceRequirementForControlResponse[];
    control: OperationalControl;
    disbled: boolean;
}

export const ArtificialIntelligenceControlAssessment = (props: ArtificialIntelligenceControlAssessmentProps) => {
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal>();
    const [assessment, setAssessment] = useState<AiOperationalControlAssessment>();
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);

    const { controlFramework, controlGroupId, controlId } = useMemo(() => {
        return getFrameworkGroupControlParts(props.control);
    }, [props.control]);

    useEffect(() => {
        const getAssessment = async (): Promise<void> => {
            try {
                const response = await props.artificialIntelligenceApi.aiOperationalControlsGet(controlFramework, controlGroupId, controlId);
                setAssessment(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };

        getAssessment();
    }, [props.artificialIntelligenceApi, controlFramework, controlGroupId, controlId]);

    if (loadingErrorOccurred) {
        return (
            <>
                <Text variant="Header2">SummIT Guide™ Control Assessment</Text>
                <hr />
                <Text>{GENERIC_ERROR_MESSAGE}</Text>
            </>
        );
    } else if (!assessment) {
        return <Placeholder />;
    }

    return (
        <>
            {displayedModal === DisplayedModal.CLEAR && (
                <ConfirmationModal
                    operationType="destructive"
                    headerText="Clear Assessment"
                    buttonText="Clear"
                    buttonIcon={ICON_DELETE_REMOVE}
                    buttonLoadingText="Clearing..."
                    areYouSureText="Are you sure you want to clear the SummIT Guide™ control assessment?"
                    hideModal={() => setDisplayedModal(undefined)}
                    performOperation={async () => {
                        await props.artificialIntelligenceApi.aiOperationalControlsDelete(controlFramework, controlGroupId, controlId);
                        setAssessment({});
                        return 'Assessment cleared.';
                    }}
                >
                    <Text>This action cannot be undone. A new SummIT Guide™ control assessment can be generated later if desired.</Text>
                </ConfirmationModal>
            )}
            {displayedModal === DisplayedModal.GENERATE && (
                <ConfirmationModal
                    operationType="nonDestructive"
                    headerText="Generate SummIT Guide™ Control Assessment"
                    buttonText="Generate"
                    buttonIcon={ICON_ADD_CREATE}
                    buttonLoadingText="Generating..."
                    areYouSureText="Are you sure you want to generate a SummIT Guide™ control assessment?"
                    hideModal={() => setDisplayedModal(undefined)}
                    performOperation={async () => {
                        await props.artificialIntelligenceApi.aiOperationalControlsCreate(controlFramework, controlGroupId, controlId);
                        setAssessment({ generation_status: AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS });
                        return 'Control assessment generation started. Refresh the page in a few minutes to see the assessment.';
                    }}
                >
                    <div className={styles.disclaimer}>
                        <GenerativeAiDisclaimer />
                    </div>
                </ConfirmationModal>
            )}

            <div className={styles.aiHeader}>
                <div className={styles.aiHeaderText}>
                    <Text variant="Header2">SummIT Guide™ Control Assessment</Text>
                    {assessment.generation_status === AiOperationalControlAssessmentGenerationStatus.SUCCESS && assessment.final_result?.timestamp && <Text noStyles>{`Generated: ${iso8601ToUsDateShort(assessment.final_result.timestamp)}`}</Text>}
                </div>
                <div className={styles.aiHeaderButtons}>
                    {assessment.final_result !== undefined && (
                        <Button variant="secondary" disabled={props.disbled || assessment.generation_status === AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS} onClick={() => setDisplayedModal(DisplayedModal.CLEAR)} isLoading={false} loadingText="Clearing..." fontAwesomeImage={ICON_DELETE_REMOVE}>
                            Clear
                        </Button>
                    )}
                    <Button variant="secondary" disabled={props.disbled || assessment.generation_status === AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS} onClick={() => setDisplayedModal(DisplayedModal.GENERATE)} isLoading={false} loadingText="Generating..." fontAwesomeImage={ICON_ADD_CREATE}>
                        Generate New
                    </Button>
                </div>
            </div>
            <hr />
            {((): JSX.Element => {
                if (assessment.generation_status === undefined) {
                    return <Text>A SummIT Guide™ control assessment has not been generated.</Text>;
                }

                switch (assessment.generation_status) {
                    case AiOperationalControlAssessmentGenerationStatus.IN_PROGRESS:
                        return <Text>The assessment is being generated. Refresh the page in a few minutes to see the assessment.</Text>;
                    case AiOperationalControlAssessmentGenerationStatus.FAILURE:
                        return <Text>{`The assessment failed to complete. ${CONTACT_INFORMATION_FOR_ERROR_MESSAGE}`}</Text>;
                    case AiOperationalControlAssessmentGenerationStatus.SUCCESS:
                        return (
                            <>
                                <div className={styles.titleAndTooltipContainer}>
                                    <Text variant="Header3">{getHumanReadableControlIdFromControl(props.control)}</Text>
                                    <div className={styles.tooltipForControl}>
                                        <FormFieldTooltip text={ellipt(controlTextToString(props.control.metadata.control_text), MAX_CHARS_FOR_TOOLTIP)} />
                                    </div>
                                </div>
                                <Text variant="Header4" color="darkGray" noStyles>
                                    Suggested effectiveness
                                </Text>
                                <div className={styles.iconAndStatusContainer}>
                                    <CircleIndicator variant={getEffectivenessVariantColor(numberAsEffectiveness(assessment.final_result!.control_analysis.control_effectiveness_rating))} />
                                    <Text noStyles>{effectivenessAsString(assessment.final_result!.control_analysis.control_effectiveness_rating)}</Text>
                                </div>
                                <Text variant="Header4" color="darkGray" noStyles>
                                    Summary
                                </Text>
                                <Text>{assessment.final_result!.control_analysis.summary}</Text>
                                <Text variant="Header4" color="darkGray" noStyles>
                                    Key observations
                                </Text>
                                <ul className={styles.list}>
                                    {assessment.final_result!.control_analysis.key_observations.map((observation, index) => (
                                        <li key={index}>
                                            <Text noStyles>{observation}</Text>
                                        </li>
                                    ))}
                                </ul>
                                {assessment.final_result!.control_analysis.improvements.length > 0 && (
                                    <>
                                        <Text variant="Header4" color="darkGray" noStyles>
                                            Suggestions
                                        </Text>
                                        <ul className={styles.list}>
                                            {assessment.final_result!.control_analysis.improvements.map((suggestion, index) => (
                                                <li key={index}>
                                                    <Text noStyles>{suggestion}</Text>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                <Text variant="Header3">Compliance Requirements</Text>
                                {assessment.final_result!.requirement_analyses.length === 0 && <Text>-</Text>}
                                {assessment.final_result!.requirement_analyses.length > 0 && (
                                    <>
                                        {assessment.final_result!.requirement_analyses.map((analysis, index) => {
                                            const requirement = props.complianceRequirements.find((requirement) => requirement.regulation === analysis.regulation && requirement.identifier === analysis.identifier);

                                            return (
                                                <div key={index} className={index === 0 ? undefined : styles.marginTop}>
                                                    <div className={styles.titleAndTooltipContainer}>
                                                        <Text variant="Header4">{makeComplianceRequirementHumanReadable(analysis.regulation, analysis.identifier)}</Text>
                                                        {/* Edge case: an AI assessment that includes Requirement XYZ has been generated, but now XYZ is not mapped to the control. To avoid more convoluted saving/fetching of data, we have decided simply to omit the tooltip in this case. */}
                                                        {requirement && requirement.text && (
                                                            <div className={styles.tooltipForComplianceRequirement}>
                                                                <FormFieldTooltip text={ellipt(requirement.text, MAX_CHARS_FOR_TOOLTIP)} />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Text variant="Header5" color="darkGray" noStyles>
                                                        Suggested status
                                                    </Text>
                                                    <div className={styles.iconAndStatusContainer}>
                                                        <FontAwesomeIcon className={analysis.status === ComplianceRequirementStatusValues.MET ? styles.green : styles.red} icon={analysis.status === ComplianceRequirementStatusValues.MET ? ICON_POSITIVE : ICON_NEGATIVE} />
                                                        <Text noStyles>{titleCaseRequirementStatus(analysis.status)}</Text>
                                                    </div>
                                                    <Text variant="Header5" color="darkGray" noStyles>
                                                        Explanation
                                                    </Text>
                                                    <Text>{analysis.explanation}</Text>
                                                    {analysis.assumptions && (
                                                        <>
                                                            <Text variant="Header5" color="darkGray" noStyles>
                                                                Assumptions
                                                            </Text>
                                                            <Text>{analysis.assumptions}</Text>
                                                        </>
                                                    )}
                                                    {analysis.suggestions.length > 0 && (
                                                        <>
                                                            <Text variant="Header5" color="darkGray" noStyles>
                                                                Suggestions
                                                            </Text>
                                                            <ul className={styles.list}>
                                                                {analysis.suggestions.map((suggestion, index) => (
                                                                    <li key={index}>
                                                                        <Text noStyles>{suggestion}</Text>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </>
                        );
                }
            })()}
        </>
    );
};
